import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  Package,
  Search,
  RefreshCw,
  ChevronRight,
  Warehouse,
  Clock,
} from "lucide-react";
import { formatLocalDateTime, STORAGE_DETAILS } from "../lib/constants";

const REFRESH_MS = 60000;

export default function StockedShipments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [storage, setStorage] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = { workflow_stage: "Stocked" };
      if (storage) params.storage_detail = storage;
      const { data } = await api.get("/shipments", { params });
      setItems(data);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [storage]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const filtered = items.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      s.MR_Number.toLowerCase().includes(needle) ||
      (s.Contents || "").toLowerCase().includes(needle) ||
      (s.Project_Station || "").toLowerCase().includes(needle) ||
      (s.Storage_Detail || "").toLowerCase().includes(needle) ||
      (s.Assigned_Crew_Leader || "").toLowerCase().includes(needle)
    );
  });

  // Group by storage location for visual clarity
  const groups = {};
  filtered.forEach((s) => {
    const key = s.Storage_Detail || "(unspecified)";
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });
  const groupOrder = STORAGE_DETAILS.filter((s) => groups[s]).concat(
    Object.keys(groups).filter((k) => !STORAGE_DETAILS.includes(k))
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 flex items-center gap-2">
            <Warehouse className="h-4 w-4" />
            Main yard inventory
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight flex items-center gap-2">
            <Package strokeWidth={2.5} className="h-7 w-7" />
            Stocked Material
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Everything currently sitting in yard storage — by bay/rack/shelf.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="stocked-refresh-btn"
          className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {lastRefresh && (
        <div className="text-xs text-slate-500 mb-4 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Last refreshed {formatLocalDateTime(lastRefresh.toISOString())} · {filtered.length} items
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-2 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="stocked-search"
            placeholder="Search MR, contents, station, storage…"
            className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white text-base focus:border-slate-900 outline-none"
          />
        </div>
        <select
          value={storage}
          onChange={(e) => setStorage(e.target.value)}
          data-testid="stocked-storage-filter"
          className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none appearance-none"
        >
          <option value="">All storage locations</option>
          {STORAGE_DETAILS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="stocked-empty">
          <div className="font-display text-xl text-slate-900 uppercase mb-1">No stocked material</div>
          <div className="text-sm text-slate-600">
            Receive a shipment and stock it to see it here.
          </div>
        </div>
      ) : (
        <div className="space-y-5" data-testid="stocked-groups">
          {groupOrder.map((g) => (
            <section key={g} data-testid={`stocked-group-${g.replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="font-display text-lg uppercase text-slate-900">{g}</div>
                <span className="rg-pill bg-slate-900 text-white">{groups[g].length}</span>
              </div>
              <ul className="space-y-2">
                {groups[g].map((s) => (
                  <li key={s.id}>
                    <Link
                      to={`/shipments/${s.id}`}
                      data-testid={`stocked-row-${s.MR_Number}`}
                      className="rg-card p-3 sm:p-4 flex items-center gap-3 hover:border-slate-900 hover:bg-slate-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-bold text-slate-900 truncate">
                          {s.MR_Number}
                        </div>
                        <div className="text-xs text-slate-700 truncate">
                          {s.Project_Station}
                          {s.Contents ? ` · ${s.Contents}` : ""}
                        </div>
                        {s.Assigned_Crew_Leader && (
                          <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">
                            Crew: <span className="font-bold text-slate-700">{s.Assigned_Crew_Leader}</span>
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
